import styles from './modal-content.module.css';

import { Button } from '@/ui/button/button';
import { useControlledCounter, useCounter } from '@/ui/use-counter/use-counter';
import { useState } from 'react';
import { CartItem, useCartItem } from '@/app/cartContext';
import { withProduct } from '@/api/product';
import { ClientProduct } from '@/api/types/product';
import { Spin } from '@/ui/spin';
import { Toppings } from './toppings/toppings';
import { ToppingCard } from './toppings/topping-card/topping-card';
import { ImageWithStub } from '@/ui/image-with-stub/image-with-stub';
import { OptionalContainer } from '@/ui/optional-container';

type Props = {
  product: ClientProduct;
  id: number;
  item?: CartItem;
  onAccept: (cartItem: CartItem) => void;
  isMobile?: boolean;
};

const mapProductToCartItem = (product: ClientProduct, quantity = 1): CartItem => {
  return { ...product, quantity, extra: undefined };
};

export const ModalContent = withProduct<Props>(
  ({ product, onAccept, item, isMobile = false }) => {
    const cartItem = item || mapProductToCartItem(product);

    const { name, price, description, weight, quantity, extra, imageUrl } = cartItem;

    const { decrementQuantity, incrementQuantity } = useCartItem(item);
    const controlledCounter = useControlledCounter(quantity, incrementQuantity, decrementQuantity);
    const uncontrolledCounter = useCounter(quantity);

    const { component: counter, count } = item ? controlledCounter : uncontrolledCounter;

    const [selectedToppings, setSelectedToppings] = useState(new Set(item?.additional?.map((e) => e.id)));

    const [selectedExtra, setSelectedExtra] = useState<number | undefined>(extra?.id);

    const totalPrice =
      count * (price +
        (product.extra?.find((e) => e.id === selectedExtra)?.price ?? 0) +
        (product.additional?.filter((e) => selectedToppings.has(e.id)).reduce((prev, { price }) => prev + price, 0) ??
          0));

    const isSelected = (id: number) => selectedToppings.has(id);

    const toggleSelected = (id: number) => {
      setSelectedToppings((prevSelected) => {
        const newSelected = new Set(prevSelected);
        if (newSelected.has(id)) {
          newSelected.delete(id);
        } else {
          newSelected.add(id);
        }
        return newSelected;
      });
    };

    const onClick = () => {
      onAccept({
        ...cartItem,
        quantity: count,
        additional: product.additional?.filter((e) => selectedToppings.has(e.id)),
        extra: product.extra?.find((e) => e.id === selectedExtra),
      });
    };

    return (
      <div className={`${styles.wrapper} ${isMobile ? styles.isMobile : ''}`}>
        <OptionalContainer className={styles.image} show={isMobile} >
          <ImageWithStub src={imageUrl} alt={'Фото товара'} className={styles.image} fill={isMobile}
            width={!isMobile ? 348 : undefined}
            height={!isMobile ? 347 : undefined} />
        </OptionalContainer>
        <div className={styles.right}>
          <OptionalContainer className={styles.title} show={isMobile} >
            <div className={styles.name}>{name}</div>
            {isMobile && <div className={styles.counter}>{counter}</div>}
          </OptionalContainer>
          <div className={styles.description}>{description}</div>
          <div className={styles.weight}>Масса: {weight} гр.</div>

          {!isMobile && <div className={styles.counter}>{counter}</div>}

          {(product.additional || product.extra) && <div className={styles.addText}>Добавить:</div>}
          {product.extra && (
            <div className={styles.extraWrapper}>
              {product.extra?.map((e) => (
                <ToppingCard
                  key={e.id}
                  data={{ name: e.name, price: e.price }}
                  isSelected={selectedExtra === e.id}
                  toggleSelected={() =>
                    setSelectedExtra((prev) => (prev === e.id ? undefined : e.id))
                  }
                />
              ))}
            </div>
          )}
          {product.additional && (
            <div className={styles.toppingWrapper}>
              <Toppings
                toppings={product.additional}
                isSelected={isSelected}
                toggleSelected={toggleSelected}
              />
            </div>
          )}

          <div className={styles.button}>
            <Button type="primary" style={{ width: '100%' }} onClick={onClick}>
              В корзину за {totalPrice} ₽
            </Button>
          </div>
        </div>
      </div >
    );
  },
  { loadingComponent: <Spin centered /> },
);
